import { useAdsgram } from '@adsgram/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AdIntentType,
  DailyLimit,
  createAdIntent,
  getAdIntent,
  getDailyLimit,
  getReward,
} from '@api'
import config from '@config'
import { sendEvent, webApp, GA_EVENTS } from '@shared'
import { MainInventoryMapper, useInventoryStoreBase } from '@store'
import { useMonetag } from './useMonetag'

const backButton = webApp?.BackButton

const INTENT_POLL_TIMEOUT = 60_000
const INTENT_POLL_INTERVAL = 500

export function useMonetization(intentType: AdIntentType,  provider: 'adsgram' | 'monetag') {
  const [isLoading, setIsLoading] = useState(false)
  const [dailyLimit, setDailyLimit] = useState<DailyLimit | null>(null)
  const inFlight = useRef(false)
  const dailyLimitRef = useRef(dailyLimit)
  const { addCard } = useInventoryStoreBase.getState().actions
  const { t } = useTranslation()

  const blockId = config.adsgramBlockIds['default'] as `${number}`
  const zoneId = config.isProd ? 10605468 : 10592306

  const adsgramController = useAdsgram({
    blockId,
    debug: false,
    onError: () => {
      sendEvent(GA_EVENTS.ADSGRAM_ERROR, { error: 'adsgram error' })
    },
  })
  const monetagController = useMonetag(zoneId)

  const showAdProvider = useMemo(
    () => (provider === 'monetag' ? monetagController.show : adsgramController.show),
    [provider, adsgramController, monetagController],
  )

  useEffect(() => {
    if (provider !== 'adsgram') return

    const handleBannerNotFound = () => {
      webApp?.showAlert(t('ad.notFoundAdError'))
      sendEvent(GA_EVENTS.ADSGRAM_NOT_FOUND, { error: 'adsgram not found' })
    }

    const handleTooLongSession = () => {
      webApp?.showAlert(t('ad.tooLongSession'))
      sendEvent(GA_EVENTS.ADSGRAM_TOO_LONG_SESSION, {
        error: 'adsgram too long session',
      })
    }

    adsgramController.addEventListener('onBannerNotFound', handleBannerNotFound)
    adsgramController.addEventListener('onTooLongSession', handleTooLongSession)

    return () => {
      adsgramController.removeEventListener('onBannerNotFound', handleBannerNotFound)
      adsgramController.removeEventListener('onTooLongSession', handleTooLongSession)
    }
  }, [adsgramController, provider])

  useEffect(() => {
    dailyLimitRef.current = dailyLimit
  }, [dailyLimit])

  useEffect(() => {
    fetchDailyLimit()
  }, [])

  const fetchDailyLimit = async () => {
    const { data } = await getDailyLimit();
    const limit = data?.[intentType === 'unlock_quest' ? 'skip_stars' : intentType]
    if (limit) {
      setDailyLimit(limit)
    }
    return limit
  }

  const onTapResolved = async () => {
    const reward = await getReward()
    if (reward.data) {
      addCard(MainInventoryMapper.mapFromBackend(reward.data))
    }
  }

  const showAd = useCallback(
    async (payload?: string, onResolved?: () => void) => {
      if (inFlight.current) {
        return { ok: false as const }
      }

      if (intentType === 'tap' && !dailyLimitRef.current) {
        return { ok: false as const }
      }
      setIsLoading(true)

      inFlight.current = true
      const prevIsClose = webApp.isClosingConfirmationEnabled
      webApp.isClosingConfirmationEnabled = true
      backButton?.hide()

      try {
        const intent = await createAdIntent(intentType, payload)
        const res = await showAdProvider()
        // @ts-expect-error
        if (res && provider === 'monetag' && res.reward_event_type !== 'valued') {
          // @ts-expect-error
          sendEvent(GA_EVENTS.MONETAG_NOT_PAID, { error: `${res.reward_event_type}` })
        }
        const start = Date.now()

        while (Date.now() - start < INTENT_POLL_TIMEOUT) {
          try {
            const { data } = await getAdIntent()
            if (!data || !intent.data) continue;

            if (data.id === intent.data.intent_id && data?.is_resolved) {
              if (intentType === 'tap') {
                await onTapResolved()
              }
              onResolved?.()
              break
            }
          } catch {}
          await new Promise((r) => setTimeout(r, INTENT_POLL_INTERVAL))
        }
      } catch (e) {
        sendEvent(GA_EVENTS.AD_ERROR, { error: `intent: ${intentType}; Error: ${e instanceof Error ? e.message : e}` })
        console.log('Ad error', e)
      } finally {
        fetchDailyLimit()
        // TODO: Mb replace it to another place? Because we are blocked by waiting intent and reward response
        inFlight.current = false
        backButton?.show()
        webApp.isClosingConfirmationEnabled = prevIsClose
        setIsLoading(false)
      }

      return { ok: true as const }
    },
    [addCard, intentType, showAdProvider]
  )

  return { showAd, dailyLimit, isLoading }
}